import { useSnackbar } from '@components/snackbar';
import { BoxStarIcon } from '@sketchnote/icons';
import { Divider } from '@sketchnote/shared-components';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { API } from 'src/api';
import { isAdvUser, isFreeUser, isProUser, PAYPLAN, PAYPLAN_TITLES } from 'src/constants/plans';
import { useAuth } from 'src/shared/hooks/use-auth';
const PlanInfo = ({ plan, onClose }: { plan: string; onClose: () => void }) => {
  const freeInfo = ['Unlimited Users', '3 Workspaces', '10 GB storage'];
  const proInfo = ['Unlimited workspaces', 'Task reminders', 'Recurring tasks'];
  const advInfo = ['All views', 'All integrations', 'Storage view', 'Unlimited guest access'];

  const infoList = useMemo(() => {
    if (isProUser(plan)) return proInfo;
    else if (isAdvUser(plan)) return advInfo;
    else return freeInfo;
  }, [plan]);

  const currentPlan = useMemo(() => PAYPLAN_TITLES[plan] ?? PAYPLAN_TITLES[PAYPLAN.FREE], [plan]);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  return (
    <div className="">
      <div className="flex">
        <div className="w-1/2 bg-gray-100 p-24 rounded-sm">
          <p className="text-lg text-blue-700 font-semibold mb-16">{currentPlan}</p>
          {infoList.map((item) => (
            <p key={item.split(' ').join('')} className="mb-8 text-gray-300 flex">
              <BoxStarIcon wrapperClass="mt-2 inline-block" />
              <p className="text-blue-800 ml-2">&nbsp;{item}</p>
            </p>
          ))}
        </div>
        <div className="pl-32">
          <p className="font-medium text-gray-999 mb-8">
            Want to get more out of your Sketchnote experience?
          </p>
          <p className="mb-16">Upgrade your plan for more features</p>
          <button
            onClick={() => {
              onClose();
              router.push(`/${mode}/${orgId}/pricing`);
            }}
            className="bg-blue-500 px-12 py-8 rounded-sm text-white"
          >
            Upgrade now
          </button>
        </div>
      </div>
    </div>
  );
};

export const Billing = ({ onClose }: { onClose: () => void }) => {
  const authData = useAuth((s) => s.userDetails);
  const router = useRouter();
  const [manageBillingUrl, setManageBillingUrl] = useState<string>('');
  const [openSnackbar] = useSnackbar();
  const plan = authData?.plan?.type;
  useEffect(() => {
    const getPortalLink = async () => {
      try {
        const response = await API.post('/payment/stripe/create-portal-session');
        if (response.data?.success) {
          setManageBillingUrl(response.data.message);
        } else {
          openSnackbar({
            text: 'There was an error reaching the payment provider. Please try again in some time.',
            snackType: 'error',
            position: 'top-center',
          });
        }
      } catch (error) {
        openSnackbar({
          text: 'There was an error connecting with the payment provider. Please try opening the settings panel again.',
          snackType: 'error',
        });
      }
    };
    if (authData?.plan.type && !isFreeUser(authData?.plan.type)) getPortalLink();
  }, []);

  const goToManageBilling = () => {
    if (manageBillingUrl) {
      const newWindow = window.open(manageBillingUrl, '_blank', 'noreferrer');
      if (newWindow) newWindow.opener = null;
    } else {
      openSnackbar({
        text: 'There was an error connecting with the payment provider. Please try opening the settings panel again.',
        snackType: 'error',
      });
    }
  };

  const handleCancelSubscription = async () => {
    const response = await API.post('/payment/stripe/create-checkout-session', {
      lookup_key: PAYPLAN.FREE,
      success_url: `${window.location.origin}${window.location.pathname}`,
      cancel_url: `${window.location.origin}${window.location.pathname}`,
    });
    if (response.data) {
      openSnackbar({
        text: 'Your subscription has been cancelled',
      });
    }
  };

  return (
    <div className="overflow-y-auto px-48" style={{ height: '59rem' }}>
      <section className="pt-32 mb-32">
        <span className="mb-8 block font-medium text-gray-600">Billing Plan</span>
        {authData?.plan.expiry && (
          <p className="text-xs text-gray-500 mt-8">
            Plan valid till{' '}
            <span className="text-gray-700">
              {format(new Date(authData?.plan.expiry), 'do MMM yyyy')}
            </span>
          </p>
        )}
      </section>

      {authData?.plan.type && (
        <section className="flex mb-36">
          {isFreeUser(authData?.plan.type) ? (
            <FreeBilling onClose={onClose} />
          ) : isProUser(authData?.plan.type) ? (
            <PlanInfo plan={authData?.plan.type} onClose={onClose} />
          ) : (
            <PlanInfo plan={authData?.plan.type} onClose={onClose} />
          )}
        </section>
      )}

      {/* <Divider /> */}
      {/* {plan && !isFreeUser(authData?.plan.type) && (
        <section className="mt-36 text-gray-600">
          <p className="mb-8 font-medium">Cancel subscription</p>
          <p className="mb-16">
            You will be charged on a pro-rated basis for each new member added to your organization.
            This will be over and above your current plan already active.
          </p>
          <div className="flex">
            <button
              onClick={handleCancelSubscription}
              className="text-red-600 rounded-sm px-24 py-8 border border-red-600 font-medium mr-16"
            >
              Cancel
            </button>
            {plan && isAdvUser(plan) && (
              <button className="text-red-600 rounded-sm px-24 py-8 border border-red-600 font-medium">
                Downgrade to Pro Plan
              </button>
            )} 
          </div>
        </section>
      )} */}
    </div>
  );
};

const FreeBilling = ({ onClose }: { onClose: () => void }) => {
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  return (
    <div className="bg-gray-100 rounded-sm p-32 w-full mb-36">
      <p className="font-semibold mb-16 ">Upgrade & get the most out of Sketchnote</p>
      <p className="text-gray-600 mb-32">
        We’re here to help with any questions you may have about plans, pricing, and supported
        features
      </p>
      <button
        onClick={() => {
          onClose();
          router.push(`/${mode}/${orgId}/pricing`);
        }}
        className="bg-blue-500 px-12 py-8 rounded-sm text-white"
      >
        Upgrade now!
      </button>
    </div>
  );
};
